import api from '@/lib/api';
import type {
  NonWorkingDay,
  PresenterOrderEntry,
  ReportMinRole,
  WorklogDailyReport,
  WorklogGridResponse,
  WorklogPortfolioResponse,
  WorklogReportAccess,
} from '@hancomum/shared';

/** /ref/users 응답 (개인 필터·대상 명단) */
export interface ReportRefUser {
  id: number;
  name: string;
  position: string | null;
  position_order: number | null;
  rank: string | null;
  department_id: number | null;
  department_name: string | null;
  as_team_id: number | null;
}

export interface GridFilter {
  start: string;
  weeks: number;
  userIds: number[];
  deptId?: number | null;
  rank?: string | null;
  today: string;
  detail?: boolean; // 업무 축(단일 사용자)에서 true → 셀에 summary/project_name(짧은 라벨) 적재
  body?: boolean; // 상세(detail) 모드에서만 true → 셀에 content(자유텍스트 본문) 적재 (교차 열람 감사 대상)
  // 발표 모드(아침 업무보고 순회) 중의 조회·선반입 표시 → 서버가 교차 열람 감사를 스킵한다.
  // queryKey에는 넣지 않는다(fetch 파라미터만) — 응답 데이터는 동일하고 감사 여부만 갈리므로,
  // 키를 나누면 발표 모드 진입/이탈 시 같은 데이터를 이중 캐시·이중 요청하게 된다.
  presenter?: boolean;
}

function gridParams(f: GridFilter) {
  const params: Record<string, string | number> = {
    start: f.start,
    weeks: f.weeks,
    today: f.today,
  };
  if (f.userIds.length > 0) params.user_ids = f.userIds.join(',');
  if (f.deptId != null) params.dept_id = f.deptId;
  if (f.rank) params.rank = f.rank;
  if (f.detail) params.detail = 1;
  if (f.body) params.body = 1;
  if (f.presenter) params.presenter = 1;
  return params;
}

export async function fetchReportAccess(): Promise<WorklogReportAccess> {
  const res = await api.get('/worklog-report/access');
  return res.data.data;
}

export async function fetchRefUsers(): Promise<ReportRefUser[]> {
  const res = await api.get('/ref/users');
  return res.data.data;
}

export async function fetchReportGrid(f: GridFilter): Promise<WorklogGridResponse> {
  const res = await api.get('/worklog-report/grid', { params: gridParams(f) });
  return res.data.data;
}

export async function fetchDailyReport(
  userId: number,
  date: string,
  presenter?: boolean,
): Promise<WorklogDailyReport> {
  const params: Record<string, string | number> = { user_id: userId, date };
  if (presenter) params.presenter = 1;
  const res = await api.get('/worklog-report/daily', { params });
  return res.data.data;
}

/** E5 발표자 담당 프로젝트 패널 — 12주 시리즈는 서버가 today 기준으로 고정 산출 */
export async function fetchPortfolio(userId: number, today: string): Promise<WorklogPortfolioResponse> {
  const res = await api.get('/worklog-report/portfolio', { params: { user_id: userId, today } });
  return res.data.data;
}

export async function fetchNonWorkingDays(): Promise<NonWorkingDay[]> {
  const res = await api.get('/worklog-report/non-working-days');
  return res.data.data;
}

export async function saveNonWorkingDays(days: NonWorkingDay[]): Promise<NonWorkingDay[]> {
  const res = await api.put('/worklog-report/non-working-days', { days });
  return res.data.data;
}

export async function fetchPresenterOrder(): Promise<PresenterOrderEntry[]> {
  const res = await api.get('/worklog-report/presenter-order');
  return res.data.data;
}

export async function savePresenterOrder(
  order: PresenterOrderEntry[],
): Promise<PresenterOrderEntry[]> {
  const res = await api.put('/worklog-report/presenter-order', { order });
  return res.data.data;
}

export async function updateReportMinRole(minRole: ReportMinRole): Promise<ReportMinRole> {
  const res = await api.put('/worklog-report/min-role', { min_role: minRole });
  return res.data.data.min_role;
}
